import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, ArrowsClockwise, DownloadSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { fmtDate } from '@/utils/dateFmt';
import { fmtNum } from '@/utils/numFmt';

const API = process.env.REACT_APP_BACKEND_URL || '';
const eur = (v) => `${fmtNum(v, 2)} €`;

function AssociateDialog({ item, onClose, onDone }) {
  const [q, setQ] = useState(item.suggested_patient_name || '');
  const [patients, setPatients] = useState([]);
  const [patient, setPatient] = useState(item.suggested_patient_id ? { user_id: item.suggested_patient_id, name: item.suggested_patient_name } : null);
  const [consults, setConsults] = useState([]);
  const [consultId, setConsultId] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setPatients([]); return undefined; }
    const t = setTimeout(() => axios.get(`${API}/api/patients?search=${encodeURIComponent(q.trim())}&limit=8`).then(r => setPatients(Array.isArray(r.data) ? r.data : [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    setConsultId('');
    if (!patient) { setConsults([]); return; }
    axios.get(`${API}/api/patients/${patient.user_id}/consultations`).then(r => setConsults((Array.isArray(r.data) ? r.data : []).filter(c => !c.payment_id))).catch(() => setConsults([]));
  }, [patient]);
  const [bDate, setBDate] = useState(item.booking_date || '');
  const [bTime, setBTime] = useState(item.booking_time || '');
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/unlinked-payments/${item.id}/associate`, { patient_id: patient.user_id, consultation_id: consultId, ...(item.is_booking ? { date: bDate, time: bTime } : {}) });
      toast.success('Pagamento associado');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao associar'); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-3xl border-brand-border max-h-[85vh] overflow-y-auto" data-testid="associate-dialog">
        <DialogHeader><DialogTitle className="font-heading text-brand-text">Associar pagamento de {eur(item.amount)}</DialogTitle></DialogHeader>
        <p className="text-xs text-brand-muted">{item.name || 'Sem nome'} · {item.email || 'sem email'} · {fmtDate(item.date)}</p>
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-3 text-brand-placeholder" />
          <Input value={q} onChange={e => { setQ(e.target.value); setPatient(null); }} placeholder="Pesquisar paciente por nome" className="pl-8 rounded-xl border-brand-border" data-testid="associate-patient-search" />
        </div>
        {!patient && patients.length > 0 && (
          <div className="border border-brand-border rounded-xl divide-y divide-brand-border/50">
            {patients.map(p => (
              <button key={p.user_id} onClick={() => { setPatient(p); setQ(p.name); }} className="w-full text-left px-3 py-2 text-sm hover:bg-brand-bg" data-testid={`associate-patient-${p.user_id}`}>
                {p.name} <span className="text-xs text-brand-muted">{p.email}</span>
              </button>
            ))}
          </div>
        )}
        {patient && item.is_booking && (
          <div className="space-y-2" data-testid="associate-booking">
            <p className="text-xs text-brand-muted">Paciente: <b className="text-brand-text">{patient.name}</b>. A reserva passa a consulta e segue o email normal de confirmação. Podes mudar a data e a hora.</p>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={bDate} onChange={e => setBDate(e.target.value)} className="rounded-xl" data-testid="associate-booking-date" />
              <Input type="time" value={bTime} onChange={e => setBTime(e.target.value)} className="rounded-xl" data-testid="associate-booking-time" />
            </div>
          </div>
        )}
        {patient && !item.is_booking && (
          <div className="space-y-2" data-testid="associate-consults">
            <p className="text-xs text-brand-muted">Paciente: <b className="text-brand-text">{patient.name}</b>. Escolhe a consulta (só aparecem consultas sem pagamento ligado).</p>
            <label className="flex items-center gap-2 text-sm"><input type="radio" checked={consultId === ''} onChange={() => setConsultId('')} data-testid="associate-no-consult" /> Sem consulta (só ligar à paciente)</label>
            {consults.map(c => (
              <label key={c.id} className="flex items-center gap-2 text-sm"><input type="radio" checked={consultId === c.id} onChange={() => setConsultId(c.id)} data-testid={`associate-consult-${c.id}`} /> {fmtDate(c.date)}{c.gross_amount ? ` · ${eur(c.gross_amount)}` : ''}</label>
            ))}
          </div>
        )}
        <button onClick={save} disabled={!patient || saving} className="w-full bg-brand-green text-white rounded-full py-3 font-medium disabled:opacity-40" data-testid="associate-confirm-btn">{saving ? 'A associar...' : 'Associar pagamento'}</button>
      </DialogContent>
    </Dialog>
  );
}

export default function UnlinkedPaymentsPanel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('por_associar');
  const [q, setQ] = useState('');
  const [assoc, setAssoc] = useState(null);
  const [since, setSince] = useState('2026-03-01');
  const [scanning, setScanning] = useState(false);
  const [report, setReport] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/admin/unlinked-payments`, { params: { status, q } }); setItems(data); } catch { setItems([]); }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);
  const scan = async () => {
    setScanning(true);
    try { const { data } = await axios.post(`${API}/api/admin/stripe-recovery/scan`, { since }); setReport(data); toast.success(`Verificação concluída: ${data.counts.nova_na_fila} novas em Por associar`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro na verificação'); } finally { setScanning(false); }
  };
  const release = async (it) => {
    if (!window.confirm('Tem a certeza? O pagamento fica marcado como devolvido e o horário é libertado.')) return;
    try { await axios.post(`${API}/api/admin/unlinked-payments/${it.id}/release`); toast.success('Horário libertado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const csv = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/stripe-recovery/report.csv`, { responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(data); a.download = 'stripe_recuperacao.csv'; a.click();
    } catch { toast.error('Erro ao descarregar o CSV'); }
  };
  return (
    <div data-testid="unlinked-payments-panel">
      <p className="text-xs text-brand-muted mb-3">Pagamentos Stripe recebidos que não ficaram ligados a uma paciente e a uma consulta. Nada é associado automaticamente: escolhe a paciente e a consulta certas.</p>
      <div className="flex flex-wrap gap-2 items-center mb-3">
        {[['por_associar', 'Por associar'], ['associado', 'Associados'], ['all', 'Todos']].map(([v, l]) => (
          <button key={v} onClick={() => setStatus(v)} className={`px-3 py-1 rounded-full text-xs border ${status === v ? 'bg-brand-green text-white border-brand-green' : 'bg-white text-brand-muted border-brand-border'}`} data-testid={`unlinked-filter-${v}`}>{l}</button>
        ))}
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Pesquisar nome, email ou session" className="max-w-[240px] h-8 rounded-full text-xs border-brand-border" data-testid="unlinked-search" />
      </div>
      {items.length === 0 ? <p className="text-center text-sm text-brand-muted py-6" data-testid="unlinked-empty">Sem pagamentos {status === 'por_associar' ? 'por associar' : 'nesta lista'}.</p> : (
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.id} className="bg-white border border-brand-border rounded-2xl p-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`unlinked-${it.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-brand-text">{it.name || 'Sem nome'} <span className="text-xs text-brand-muted font-normal">{it.email}</span></p>
                {it.is_booking && <p className="text-[11px] font-medium text-brand-green" data-testid={`unlinked-booking-${it.id}`}>Marcação reservada: {fmtDate(it.booking_date)} às {it.booking_time}</p>}
                <p className="text-[11px] text-brand-muted break-all">{fmtDate(it.date)} · {it.session_id}</p>
                <p className="text-[11px] text-amber-700">{it.reason}{it.suggested_patient_name ? ` · sugestão: ${it.suggested_patient_name}` : ''}</p>
                {(it.family_suggestions || []).length > 0 && <p className="text-[11px] text-brand-muted" data-testid={`unlinked-family-${it.id}`}>Fichas da família: {it.family_suggestions.map(f => f.name).join(', ')}</p>}
              </div>
              <div className="flex items-center gap-3">
                <span className="font-heading font-semibold text-brand-text" data-testid={`unlinked-amount-${it.id}`}>{eur(it.amount)}</span>
                {it.status === 'por_associar'
                  ? <>{it.is_booking && <button onClick={() => release(it)} className="border border-brand-border text-brand-muted rounded-full px-3 py-1.5 text-xs" data-testid={`unlinked-release-${it.id}`}>Devolvido</button>}<button onClick={() => setAssoc(it)} className="bg-brand-green text-white rounded-full px-4 py-1.5 text-xs font-medium" data-testid={`unlinked-associate-${it.id}`}>Associar</button></>
                  : <span className="text-[11px] bg-green-50 text-green-700 px-2 py-0.5 rounded-full" data-testid={`unlinked-done-${it.id}`}>Associado</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6 pt-4 border-t border-brand-border/60">
        <p className="text-xs font-medium text-brand-text mb-2">Verificar sessões pagas na Stripe</p>
        <div className="flex flex-wrap gap-2 items-center">
          <Input type="date" value={since} onChange={e => setSince(e.target.value)} className="max-w-[160px] h-8 rounded-full text-xs border-brand-border" data-testid="scan-since" />
          <button onClick={scan} disabled={scanning} className="border border-brand-border text-brand-green rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1 disabled:opacity-50" data-testid="scan-stripe-btn"><ArrowsClockwise size={12} /> {scanning ? 'A verificar...' : 'Verificar Stripe'}</button>
          <button onClick={csv} className="text-xs text-brand-muted flex items-center gap-1" data-testid="scan-csv-btn"><DownloadSimple size={12} /> CSV</button>
        </div>
        {report && (
          <p className="text-xs text-brand-muted mt-2" data-testid="scan-summary">Chave {report.stripe_key_mode}. {report.total_sessions_paid} sessões pagas desde {fmtDate(report.since)}: {report.counts.ligada} ligadas, {report.counts.nova_na_fila} novas em Por associar, {report.counts.ja_na_fila} já na fila, {report.counts.possivelmente_manual} parecem já associadas à mão.</p>
        )}
      </div>
      {assoc && <AssociateDialog item={assoc} onClose={() => setAssoc(null)} onDone={() => { setAssoc(null); load(); }} />}
    </div>
  );
}
